import { computed, ref, reactive, nextTick } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import defaultColors from '@/core/colors'

export default function useBooks ({ props, emit, shouldChangeDropdownIconColor = true }) {
  const { state, getters, dispatch } = useStore()
  const router = useRouter()

  // refs
  const titleInputRef = ref(null)
  const menuRef = ref(null)
  const inviteModalRef = ref(null)

  // data
  const showDropdown = ref(false)

  const temporaryTitle = ref(props.book.title || '')
  const temporarySubTitle = ref(props.book.subTitle || '')

  let timerEdit = null
  const menuIsOpen = ref(false)

  const localBook = reactive(JSON.parse(JSON.stringify(props.book)) || {})

  const isEdit = reactive({
    title: false,
    subTitle: false
  })

  // computed
  const isBookInstructionAccepted = computed(() => state.profile.user.profile.showBookOnboarding)
  const isPoseidonTheme = computed(() => state.settings.settings.theme === 'Poseidon')
  const isDraculaTheme = computed(() => state.settings.settings.theme === 'Dracula')
  const isTwilightTheme = computed(() => getters.isTwilightTheme)
  const isTeamAccount = computed(() => getters.isTeamAccount)
  const isDarkMode = computed(() => getters.isDarkMode)
  const isMagenta = computed(() => getters.isMagenta)
  const team = computed(() => state.team.team)

  const dropdownIconClasses = computed(() => {
    let classes = props.book.color && shouldChangeDropdownIconColor ? 'text-grey-main ' : 'text-app-color '
    classes = classes + 'icon-more-horizontal text-20 group-hover:opacity-100 transition-color duration-300'

    return classes
  })

  const menuHoverBg = computed(() => {
    const prefix = menuIsOpen.value ? '' : 'hover:'
    if (props.book.color && shouldChangeDropdownIconColor) return `${prefix}bg-menu-hover px-5 py-3 rounded-6 anim-color-200`
    return `${isDarkMode.value ? `${prefix}bg-dark-500` : `${prefix}bg-menu-hover`} px-5 py-3 rounded-6 anim-color-200`
  })

  const projects = computed(() => {
    if (props.book.projects.length === 0) return '1 page'
    if (props.book.projects.length === 1) return '1 page'

    return `${props.book.projects.length} pages`
  })

  const bookColor = computed(() => {
    const color = defaultColors.bookColors.find(c => c.name === props.book.color)

    return color || { main: null, accent: null }
  })

  // functions
  function toggleMenu (value) {
    menuIsOpen.value = value
  }

  function goToBook () {
    if ((!isEdit.title || !isEdit.subTitle) && !props.disableDrag) {
      isTeamAccount.value
        ? router.push({ name: 'TeamBook', params: { team: team.value.link, id: props.book.id } })
        : router.push({ name: 'Book', params: { id: props.book.id } })
    }
  }

  function toggleFullEdit () {
    isEdit.title = true
    isEdit.subTitle = true

    emit('update:modelValue', true)

    nextTick(_ => {
      menuRef.value.close()
      titleInputRef.value.focus()
      titleInputRef.value.setSelectionRange(0, 9999)
    })
  }

  function cancelEdit () {
    isEdit.title = false
    isEdit.subTitle = false
    emit('update:modelValue', false)

    if (localBook.title.trim() !== temporaryTitle.value.trim() || localBook.subTitle.trim() !== temporarySubTitle.value) {
      save()
    }

    if (localBook.title.trim() === '' || localBook.subTitle.trim() === '') {
      localBook.title = temporaryTitle.value
      localBook.subTitle = temporarySubTitle.value
    }
  }

  function save () {
    if (localBook.title.trim() === '' || localBook.subTitle.trim() === '') return

    temporaryTitle.value = localBook.title
    temporarySubTitle.value = localBook.subTitle

    dispatch('editBook', localBook)
  }

  function edit () {
    clearTimeout(timerEdit)

    timerEdit = setTimeout(() => {
      save()
    }, 1000)
  }

  function deleteBook () {
    menuRef.value.close()
    emit('delete')
  }

  function openInviteModal () {
    setTimeout(() => (showDropdown.value = !showDropdown.value), 100)
  }

  return {
    // data
    showDropdown,
    menuIsOpen,
    localBook,
    isEdit,
    // refs
    inviteModalRef,
    menuRef,
    titleInputRef,
    // computed
    isBookInstructionAccepted,
    dropdownIconClasses,
    isTeamAccount,
    isDarkMode,
    projects,
    isPoseidonTheme,
    isDraculaTheme,
    isTwilightTheme,
    isMagenta,
    menuHoverBg,
    bookColor,
    // function
    toggleFullEdit,
    goToBook,
    edit,
    cancelEdit,
    toggleMenu,
    deleteBook,
    openInviteModal
  }
}
